import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ConsentModal from '../components/ConsentModal';
import api from '../utils/api';

const Settings = () => {
    const { user, updateUser } = useAuth();
    const [journalSharingEnabled, setJournalSharingEnabled] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [consentStatus, setConsentStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchConsentStatus();
    }, []);

    const fetchConsentStatus = async () => {
        try {
            const response = await api.get('/consent/status');
            setConsentStatus(response.data);
            setJournalSharingEnabled(response.data.hasConsent);
        } catch (error) {
            console.error('Error fetching consent status:', error);
        }
    };

    const handleToggleChange = (e) => {
        const isChecked = e.target.checked;

        if (isChecked) {
            // User wants to enable sharing - show confirmation dialog
            setShowConfirmDialog(true);
        } else {
            // User wants to disable sharing - revoke consent
            handleRevokeConsent();
        }
    };

    const handleConfirmSharing = () => {
        setShowConfirmDialog(false);
        setShowConsentModal(true);
    };

    const handleConsentSubmit = async (consentData) => {
        setLoading(true);
        try {
            await api.post('/consent/submit', consentData);
            setMessage('Consent submitted successfully! Your journal is now shared with your naturopath.');
            setJournalSharingEnabled(true);
            setShowConsentModal(false);
            await fetchConsentStatus();

            // Clear message after 5 seconds
            setTimeout(() => setMessage(''), 5000);
        } catch (error) {
            console.error('Error submitting consent:', error);
            setMessage(error.response?.data?.message || 'Failed to submit consent. Please try again.');
            setJournalSharingEnabled(false);
        } finally {
            setLoading(false);
        }
    };

    const handleRevokeConsent = async () => {
        if (!window.confirm('Are you sure you want to stop sharing your journal? This will immediately restrict naturopath access to your journal entries.')) {
            return;
        }

        setLoading(true);
        try {
            await api.post('/consent/revoke');
            setMessage('Journal sharing has been disabled. Your naturopath no longer has access to your journal.');
            setJournalSharingEnabled(false);
            await fetchConsentStatus();

            // Clear message after 5 seconds
            setTimeout(() => setMessage(''), 5000);
        } catch (error) {
            console.error('Error revoking consent:', error);
            setMessage(error.response?.data?.message || 'Failed to revoke consent. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="settings">
            <h1>Settings</h1>

            {message && (
                <div className={`alert ${message.includes('success') || message.includes('disabled') ? 'alert-success' : 'alert-error'}`} style={{ marginTop: '24px' }}>
                    {message}
                </div>
            )}

            <div className="card" style={{ marginTop: '24px' }}>
                <h2 style={{ marginBottom: '16px' }}>Journal Sharing</h2>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <label style={{ marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                            Share journal with a naturopath
                        </label>
                        <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                            Allow a licensed naturopath to view your journal entries for health guidance
                        </p>
                        {consentStatus?.consent && (
                            <p style={{ fontSize: '13px', color: '#8b7355', marginTop: '8px' }}>
                                Consent signed on {new Date(consentStatus.consent.signedAt).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={journalSharingEnabled}
                            onChange={handleToggleChange}
                            disabled={loading}
                        />
                        <span className="toggle-slider"></span>
                    </label>
                </div>
            </div>

            <div className="card" style={{ marginTop: '24px' }}>
                <h2 style={{ marginBottom: '16px' }}>Profile Information</h2>
                <div className="form-group">
                    <label>Full Name</label>
                    <input type="text" defaultValue={user?.fullName} />
                </div>
                <div className="form-group">
                    <label>Email</label>
                    <input type="email" defaultValue={user?.email} disabled />
                </div>
                <div className="form-group">
                    <label>Time Zone</label>
                    <input type="text" defaultValue={user?.timeZone} />
                </div>
                <button className="btn btn-primary">Save Changes</button>
            </div>

            <div className="card" style={{ marginTop: '24px' }}>
                <h2 style={{ marginBottom: '16px' }}>Preferred Units</h2>
                <div className="form-group">
                    <label>Weight</label>
                    <select defaultValue={user?.preferredUnits?.weight || 'kg'}>
                        <option value="kg">Kilograms (kg)</option>
                        <option value="lb">Pounds (lb)</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>Height</label>
                    <select defaultValue={user?.preferredUnits?.height || 'cm'}>
                        <option value="cm">Centimeters (cm)</option>
                        <option value="in">Inches (in)</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>Glucose</label>
                    <select defaultValue={user?.preferredUnits?.glucose || 'mmol/L'}>
                        <option value="mmol/L">mmol/L</option>
                        <option value="mg/dL">mg/dL</option>
                    </select>
                </div>
                <button className="btn btn-primary">Save Units</button>
            </div>

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
                    <div className="confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3>Share Journal with Naturopath</h3>
                        <p>
                            Your journal entries are only visible to you. Clicking 'Okay' provides a
                            naturopath with access to view your journal entries. You can opt out at any time.
                        </p>
                        <div className="dialog-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowConfirmDialog(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleConfirmSharing}
                            >
                                Okay, Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Consent Modal */}
            <ConsentModal
                isOpen={showConsentModal}
                onClose={() => {
                    setShowConsentModal(false);
                    setJournalSharingEnabled(false);
                }}
                onSubmit={handleConsentSubmit}
                userName={user?.fullName}
            />
        </div>
    );
};

export default Settings;
